import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { MobileFilters } from '../../components/ui/MobileFilters';
import {
  OrdinaFiltra, perData, perNumero, perTesto, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina,
} from '../../components/ui/OrdinaFiltra';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur, fmtDate } from '../../lib/utils';
import { toast } from '../../stores/toast.store';
import { plurale } from './adminApi';
import { COME_LABEL, commercialistaQuery, dataLunga, type PagamentoDaComunicare } from './banca/bancaApi';

/**
 * Banca › Da comunicare al commercialista (era "Chiuse qui").
 *
 * Le fatture che abbiamo segnato pagate DENTRO Masamune OS e che su
 * FattureInCloud risultano ancora da pagare: su FattureInCloud non si scrive
 * mai (Lorenzo, 19/09/2026), quindi le due cose divergono per costruzione.
 * Questa è la lista di quella divergenza, cioè di cosa va detto allo studio
 * Glass perché lo registri. Quando lo registrano, al primo aggiornamento di
 * FattureInCloud la riga sparisce da sola.
 *
 * "Copia l'elenco" prepara il testo da mandare: documento, cliente o
 * fornitore, importo, data e bonifico. Niente id interni (il "lotto" di prima
 * finiva a schermo nel "Perché").
 *
 * Le fatture che su FattureInCloud non ci sono non entrano: allo studio non
 * c'è niente da dire. Si contano in una riga sotto.
 *
 * Stato in URL: q · ordina · lato · bonifico
 *
 * Ordina e filtra (24/9): nel browser, la lista arriva intera. "Copia
 * l'elenco" copia quello che si vede (con i filtri, solo quelli in lista).
 */
const ORDINI: OpzioneOrdina<PagamentoDaComunicare>[] = [
  { key: 'lato', label: 'Prima gli incassi, poi i pagamenti', criteri: [perTesto((r) => r.lato), perData((r) => r.pagataIl, 'decrescente')] },
  { key: 'recenti', label: 'Pagate più di recente prima', criteri: [perData((r) => r.pagataIl, 'decrescente')] },
  { key: 'importo', label: 'Prima gli importi più alti', criteri: [perNumero((r) => r.importo, 'decrescente')] },
  { key: 'nome', label: 'Cliente o fornitore dalla A alla Z', criteri: [perTesto((r) => r.nome), perData((r) => r.pagataIl, 'decrescente')] },
];
const FILTRI: FiltroDef<PagamentoDaComunicare>[] = [
  {
    param: 'lato', label: 'Tipo di pagamento',
    opzioni: [
      { value: 'INCASSO', label: 'Solo gli incassi dai clienti' },
      { value: 'USCITA', label: 'Solo i pagamenti ai fornitori' },
    ],
    test: (r, v) => r.lato === v,
  },
  {
    param: 'bonifico', label: 'Bonifico in banca',
    opzioni: [{ value: 'senza', label: 'Solo quelle senza bonifico collegato' }],
    test: (r) => !r.bonifico,
  },
];

export function ChiuseQuiTab() {
  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  const q = useQuery(commercialistaQuery(search));
  const tutte = q.data?.items;
  const ordina = useOrdina(ORDINI);
  const filtri = useFiltri(FILTRI, tutte);
  const items = useMemo(() => ordina.applica(filtri.applica(tutte ?? [])), [tutte, ordina, filtri]);
  const totali = q.data?.totali;
  const cercando = Boolean(search.trim()) || filtri.attivi > 0;

  const copia = () => copiaElenco(items, cercando);

  return (
    <>
      <MobileFilters attivi={(search.trim() ? 1 : 0) + filtri.attivi} label="Cerca, ordina e filtra">
      <div className="filterbar filterbar--float mb-4 flex-wrap">
        <div className="tb-search flex-1" style={{ minWidth: 200, margin: 0 }}>
          <Icon name="search" size={12} />
          <input aria-label="Cerca fra i pagamenti da comunicare" placeholder="Cerca cliente, fornitore o numero di fattura…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {totali && (totali.incassi > 0 || totali.uscite > 0) && (
          <span className="mute text-[12px] bn-totali">
            {totali.incassi > 0 && <>incassi da comunicare <strong className="mono">€ {fmtEur(totali.incassi, true)}</strong></>}
            {totali.incassi > 0 && totali.uscite > 0 && ' · '}
            {totali.uscite > 0 && <>pagamenti da comunicare <strong className="mono">€ {fmtEur(totali.uscite, true)}</strong></>}
          </span>
        )}
        {/* Ordina tolto dalla vista il 26/10/2026 (l'ordine resta: prima gli incassi). */}
        <OrdinaFiltra filtri={filtri} />
        <button className="btn btn-sm d-only" disabled={!items.length} onClick={copia} title="Copia il testo pronto da mandare allo studio Glass">
          <Icon name="copy" size={10} /> Copia l’elenco
        </button>
      </div>
      </MobileFilters>

      <div className="adm-note">
        <Icon name="help" size={12} className="mute" />
        <span>
          Segnate pagate qui, ma ancora aperte su FattureInCloud: manda l’elenco allo studio Glass. Quando le registrano, spariscono da sole.
        </span>
      </div>

      {q.isLoading && !q.data ? (
        <div className="ed-section"><table className="tbl tbl--ed"><tbody><SkeletonTable rows={5} cols={5} withAvatar={false} /></tbody></table></div>
      ) : items.length === 0 ? (
        cercando
          ? <EmptyState brand title="Nessun pagamento con questi filtri" description="Togli i filtri o azzera la ricerca per vedere tutti i pagamenti da comunicare al commercialista."
              action={filtri.attivi > 0 ? <button className="btn" onClick={filtri.clear}>Togli i filtri</button> : undefined} />
          : <EmptyState brand icon="check" title="Niente da comunicare al commercialista" description="Ogni pagamento segnato qui risulta anche su FattureInCloud." />
      ) : (
        <div className="ed-section">
          <div className="data-hd">
            <span className="t-micro">Fatture segnate pagate qui e ancora aperte su FattureInCloud</span>
          </div>
          {/* `tbl--static`: sul telefono ogni riga è una scheda, ma qui non si
              apre niente (è una lista da leggere), quindi niente chevron. */}
          <table className="tbl tbl--ed tbl--card tbl--static">
            {/* 26/10/2026: a 1280 la prima colonna restava larga 60px ("EMME…"). Il tipo di
                pagamento scende sotto il nome, "Come l'abbiamo segnata" passa nel suggerimento
                della riga: restano quattro colonne. */}
            <thead>
              <tr>
                <th>Fattura</th>
                <th style={{ width: 150 }}>Pagata il</th>
                <th className="num" style={{ width: 130 }}>Importo</th>
                <th style={{ width: '30%' }}>Bonifico in banca</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => <Row key={`${r.lato}-${r.id}`} r={r} />)}
            </tbody>
          </table>
        </div>
      )}

      {q.data && q.data.soloNostre > 0 && (
        <div className="mute t-small" style={{ marginTop: 12 }}>
          {plurale(q.data.soloNostre, 'altra fattura segnata pagata qui non è', 'altre fatture segnate pagate qui non sono')} su FattureInCloud: allo studio non c’è niente da dire.
        </div>
      )}

      {/* Telefono: l'azione della vista sta in basso, dove arriva il pollice (mobile.md §4). */}
      {items.length > 0 && (
        <div className="m-actionbar m-only">
          <button className="btn btn-primary" onClick={copia}>
            <Icon name="copy" size={12} /> Copia l’elenco per il commercialista
          </button>
        </div>
      )}
    </>
  );
}

const rataDi = (r: PagamentoDaComunicare) => (r.rateTotali > 1 ? `, rata ${r.rata} di ${r.rateTotali}` : '');

/** Le colonne "Tipo di pagamento" e "Come l'abbiamo segnata", nascoste il 26/10/2026 (stavano strette a 1280). */
const MOSTRA_DETTAGLI = false;

function Row({ r }: { r: PagamentoDaComunicare }) {
  return (
    <tr style={{ cursor: 'default' }} title={`${COME_LABEL[r.come]}${r.perche ? `: ${r.perche}` : ''}`}>
      <td data-m="hd" className="adm-clip">
        <div className="font-semibold truncate" title={r.ragioneSociale}>{r.nome}</div>
        <div className="text-[12px] mute">
          {r.lato === 'INCASSO' ? 'Incasso dal cliente' : 'Pagamento al fornitore'}, fattura {r.numero ?? 'senza numero'}{rataDi(r)}
        </div>
      </td>
      {/* Il tipo di pagamento (era un chip in una colonna sua) sta sotto il nome dal 26/10/2026. */}
      {MOSTRA_DETTAGLI && (
        <td data-m="key" data-l="Tipo di pagamento">
          {r.lato === 'INCASSO' ? <Chip tone="ok">Incasso dal cliente</Chip> : <Chip>Pagamento al fornitore</Chip>}
        </td>
      )}
      <td data-m="key" data-l="Pagata il" style={{ whiteSpace: 'nowrap' }}>
        <div title={r.segnataIl ? `Segnata pagata qui il ${fmtDate(r.segnataIl)}` : undefined}>{dataLunga(r.pagataIl) || '—'}</div>
      </td>
      <td data-m="key" data-l="Importo" className="num mono font-semibold" style={{ whiteSpace: 'nowrap' }}>€ {fmtEur(r.importo, true)}</td>
      {/* In un <div>: `.adm-clip` tronca solo i figli di blocco. Nascosta dal 26/10/2026 (sta nel title della riga). */}
      {MOSTRA_DETTAGLI && (
        <td data-m="off" data-l="Come l’abbiamo segnata" className="adm-clip">
          <div title={r.perche ?? undefined}>{COME_LABEL[r.come]}</div>
          {r.perche && <div className="text-[12px] mute" title={r.perche}>{r.perche}</div>}
        </td>
      )}
      <td data-m="row" data-l="Bonifico in banca" className="adm-clip">
        {r.bonifico ? (
          <>
            <div className="truncate" title={r.bonifico.nome}>{r.bonifico.nome}</div>
            {/* La data non si ripete: è quella di "Pagata il" (la data di accredito del bonifico). */}
            {r.bonifico.conto && (
              <div className="text-[12px] mute">{r.lato === 'INCASSO' ? 'arrivato sul' : 'uscito dal'} {r.bonifico.conto}</div>
            )}
          </>
        ) : <div className="mute">nessun bonifico collegato</div>}
      </td>
    </tr>
  );
}

// ── Il testo per lo studio ───────────────────────────────────────────────────

/**
 * Il testo da incollare nella mail o nel messaggio allo studio Glass. Frasi
 * intere, elenco numerato, niente trattini: è un messaggio a un cliente.
 * Date esatte (allo studio servono quelle), totali per verso.
 */
function testoPerCommercialista(items: PagamentoDaComunicare[], oggi = new Date()): string {
  const incassi = items.filter((r) => r.lato === 'INCASSO');
  const pagamenti = items.filter((r) => r.lato === 'USCITA');
  const riga = (r: PagamentoDaComunicare, i: number) => {
    const cosa = `${i + 1}. Fattura ${r.numero ?? 'senza numero'}${rataDi(r)} di ${r.ragioneSociale}: € ${fmtEur(r.importo, true)} ${r.lato === 'INCASSO' ? 'incassati' : 'pagati'}`;
    if (!r.bonifico) return `${cosa} il ${fmtDate(r.pagataIl)}, senza bonifico collegato.`;
    // Di solito la data del pagamento È quella del bonifico: si scrive una volta.
    const altraData = r.pagataIl && r.pagataIl !== r.bonifico.data ? ` il ${fmtDate(r.pagataIl)},` : '';
    const conto = r.bonifico.conto ? ` (${r.bonifico.conto})` : '';
    return `${cosa}${altraData} con bonifico del ${fmtDate(r.bonifico.data)} ${r.lato === 'INCASSO' ? 'da' : 'a'} ${r.bonifico.nome}${conto}.`;
  };
  const somma = (rows: PagamentoDaComunicare[]) => rows.reduce((s, r) => s + r.importo, 0);
  const parti: string[] = [
    'Pagamenti da registrare su FattureInCloud',
    `Li abbiamo segnati pagati nel nostro gestionale, ma su FattureInCloud risultano ancora aperti (elenco al ${fmtDate(oggi)}).`,
  ];
  if (incassi.length) {
    parti.push('', 'Incassi dai clienti', ...incassi.map(riga), `Totale incassi: € ${fmtEur(somma(incassi), true)}`);
  }
  if (pagamenti.length) {
    parti.push('', 'Pagamenti ai fornitori', ...pagamenti.map(riga), `Totale pagamenti: € ${fmtEur(somma(pagamenti), true)}`);
  }
  return parti.join('\n');
}

export async function copiaElenco(items: PagamentoDaComunicare[], soloRicerca: boolean) {
  if (!items.length) return;
  const testo = testoPerCommercialista(items);
  try {
    await navigator.clipboard.writeText(testo);
  } catch {
    // Fuori da https o senza permesso: la vecchia strada, un campo nascosto.
    const ta = document.createElement('textarea');
    ta.value = testo;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    ta.remove();
    if (!ok) { toast.alert('Elenco non copiato', 'Il browser non ha dato il permesso: riprova.'); return; }
  }
  toast.ok(
    `${plurale(items.length, 'pagamento copiato', 'pagamenti copiati')}${soloRicerca ? ' (solo quelli in lista)' : ''}`,
    'Incollalo nella mail o nel messaggio allo studio Glass.',
  );
}
